import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import "../../assets/scss/custom/components/bottom-sheet-modals.scss";
import closeIcon from "../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * BottomSheetModal — shared shell used by every bottom-sheet modal in the
 * app (Restaurant Detail, Voucher Detail, Redeem flow, Connect Account,
 * etc.). Built on reactstrap's <Modal>/<ModalBody> — the same modal
 * primitive used everywhere else in the Minible theme — re-anchored to the
 * bottom edge of the viewport and stretched full-width purely via CSS.
 * ESC, backdrop-click, and body-scroll-lock all still come from reactstrap.
 * Drag-to-dismiss (mouse + touch) is handled here once so every consumer
 * gets it for free.
 * ---------------------------------------------------------------------- */

const BottomSheetModal = ({ isOpen, onClose, labelledBy, children, compact }) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);

  const handleDragStart = useCallback((clientY) => {
    startYRef.current = clientY;
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((clientY) => {
    const deltaY = clientY - startYRef.current;
    if (deltaY > 0) setDragY(deltaY);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragY > 100) onClose();
    setDragY(0);
    setIsDragging(false);
    startYRef.current = 0;
  }, [dragY, onClose]);

  const handleMouseDown = useCallback((e) => handleDragStart(e.clientY), [handleDragStart]);
  const handleMouseMove = useCallback((e) => handleDragMove(e.clientY), [handleDragMove]);
  const handleMouseUp = useCallback(() => handleDragEnd(), [handleDragEnd]);

  const handleTouchStart = useCallback(
    (e) => handleDragStart(e.touches[0].clientY),
    [handleDragStart]
  );
  const handleTouchMove = useCallback(
    (e) => handleDragMove(e.touches[0].clientY),
    [handleDragMove]
  );
  const handleTouchEnd = useCallback(() => handleDragEnd(), [handleDragEnd]);

  const handleDragHandleClick = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!isDragging) return undefined;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      modalClassName="sheet-wrapper"
      className="sheet-dialog"
      contentClassName={`sheet-content ${compact ? 'sheet-content--compact' : ''}`}
      backdropClassName="sheet-backdrop"
      labelledBy={labelledBy}
    >
      <ModalBody className={`sheet-body ${compact ? 'sheet-body--compact' : ''}`}>
        <button
          type="button"
          className="sheet-close-button"
          onClick={onClose}
          aria-label="Close modal"
        >
          <img src={closeIcon} alt="Close" />
        </button>
        <div
          className="sheet-drag-handle-row"
          onClick={handleDragHandleClick}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div
            className="sheet-drag-handle"
            style={{
              transform: `translateY(${dragY}px)`,
              cursor: isDragging ? "grabbing" : "pointer",
            }}
          />
        </div>

        <div className="sheet-inner">{children}</div>
      </ModalBody>
    </Modal>
  );
};

BottomSheetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  labelledBy: PropTypes.string,
  children: PropTypes.node.isRequired,
  compact: PropTypes.bool,
};

BottomSheetModal.defaultProps = {
  labelledBy: undefined,
  compact: false,
};

export default BottomSheetModal;
